import React, { type FC, useEffect } from 'react';

import { Box } from '../Box';
import { Button } from '../Button';
import { Text } from '../Typography';
import type { CtaCardProps } from './CtaCard.types';

/**
 * CtaCard - A promotional card component for displaying CTAs
 *
 * Used to display contextual promotions like LandTrust+ benefits.
 * Content is driven by props (no hardcoded variants).
 */
const CtaCard: FC<CtaCardProps> = ({
  title,
  description,
  ctaLabel,
  ctaUrl,
  ctaOpenInNewTab = false,
  onDisplay,
  onCtaClick,
  className,
}) => {
  // Fire onDisplay callback when component mounts
  useEffect(() => {
    onDisplay?.();
  }, [onDisplay]);

  const showButton = ctaUrl || onCtaClick;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="var(--spacing-4)"
      padding="var(--spacing-4)"
      borderRadius="var(--radius-lg)"
      backgroundColor="var(--surface-success)"
      textAlign="center"
      className={className}
    >
      <Box
        display="flex"
        flexDirection="column"
        gap="var(--spacing-1)"
        alignItems="center"
      >
        <Text size="sm" fontWeight="bold" color="var(--color-primary-500)">
          {title}
        </Text>

        <Text size="xs" fontWeight="semibold" color="var(--color-primary-400)">
          {description}
        </Text>
      </Box>

      {showButton && (
        <Box display="flex" justifyContent="center">
          {ctaUrl ? (
            <Button
              as="a"
              href={ctaUrl}
              variant="primary"
              size="sm"
              onClick={onCtaClick}
              target={ctaOpenInNewTab ? '_blank' : undefined}
              rel={ctaOpenInNewTab ? 'noopener noreferrer' : undefined}
            >
              {ctaLabel}
            </Button>
          ) : (
            <Button variant="primary" size="sm" onClick={onCtaClick}>
              {ctaLabel}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

CtaCard.displayName = 'CtaCard';

export default CtaCard;
